﻿/*!
    *
    * Wijmo Library 5.20251.31
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcGauge from '@mescius/wijmo.gauge';
var WjLinearGaugeBehavior = /** @class */ (function (_super) {
    __extends(WjLinearGaugeBehavior, _super);
    function WjLinearGaugeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjLinearGaugeBehavior.tag = 'wj-linear-gauge';
    WjLinearGaugeBehavior.props = [
        'isDisabled',
        'tabOrder',
        'value',
        'min',
        'max',
        'origin',
        'isReadOnly',
        'handleWheel',
        'step',
        'format',
        'thickness',
        'hasShadow',
        'isAnimated',
        'showText',
        'showTicks',
        'showTickText',
        'showRanges',
        'stackRanges',
        'thumbSize',
        'tickSpacing',
        'getText',
        'direction'
    ];
    WjLinearGaugeBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjLinearGaugeBehavior.changeEvents = {
        'valueChanged': ['value'],
    };
    WjLinearGaugeBehavior.classCtor = function () { return wjcGauge.LinearGauge; };
    WjLinearGaugeBehavior.modelProp = 'value';
    return WjLinearGaugeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.gauge.LinearGauge} control.
 *
 * The <b>wj-linear-gauge</b> component may contain
 * a {@link wijmo.vue2.gauge.WjRange} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.gauge.LinearGauge} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 *
 * The example below shows how to instantiate and initialize a
 * {@link wijmo.gauge.LinearGauge} control using Vue markup:
 *
 * <pre>&lt;wj-linear-gauge
 *     :min="0" :max="1000" :step="50" :is-read-only="false"
 *     format="c0" :thumb-size="20"
 *     :show-ranges="false"
 *     :value="sales"
 *     :value-changed="salesChanged"&gt;
 *     &lt;wj-range wj-property="face" :thickness="0.5"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range wj-property="pointer" :thickness="0.5"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="0" :max="333" color="red"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="333" :max="666" color="gold"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="666" :max="1000" color="green"&gt;
 *     &lt;/wj-range&gt;
 * &lt;/wj-linear-gauge&gt;</pre>
 *
 * The code <b>min</b>, <b>max</b>, <b>step</b>, and <b>isReadOnly</b> properties
 * to define the range of the gauge and to allow users to edit its value.
 * Next, it binds the gauge's <b>value</b> property to a <b>sales</b> variable
 * in the controller.
 *
 * Then it sets the <b>format</b>, <b>thumbSize</b>, and <b>showRanges</b>
 * properties to define the appearance of the gauge. Finally, the markup sets
 * the thickness of the <b>face</b> and <b>pointer</b> ranges, and extra ranges
 * that will control the color of the <b>value</b> range depending on the gauge's
 * current value.
 */
export var WjLinearGauge = WjLinearGaugeBehavior.register();
function registerV3WjLinearGauge(app) {
    app.component(WjLinearGaugeBehavior.tag, WjLinearGauge);
}
var WjBulletGraphBehavior = /** @class */ (function (_super) {
    __extends(WjBulletGraphBehavior, _super);
    function WjBulletGraphBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjBulletGraphBehavior.tag = 'wj-bullet-graph';
    WjBulletGraphBehavior.props = [
        'isDisabled',
        'tabOrder',
        'value',
        'min',
        'max',
        'origin',
        'isReadOnly',
        'handleWheel',
        'step',
        'format',
        'thickness',
        'hasShadow',
        'isAnimated',
        'showText',
        'showTicks',
        'showTickText',
        'showRanges',
        'stackRanges',
        'thumbSize',
        'tickSpacing',
        'getText',
        'direction',
        'target',
        'good',
        'bad'
    ];
    WjBulletGraphBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjBulletGraphBehavior.changeEvents = {
        'valueChanged': ['value'],
    };
    WjBulletGraphBehavior.classCtor = function () { return wjcGauge.BulletGraph; };
    WjBulletGraphBehavior.modelProp = 'value';
    return WjBulletGraphBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.gauge.BulletGraph} control.
 *
 * The <b>wj-bullet-graph</b> component may contain
 * a {@link wijmo.vue2.gauge.WjRange} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.gauge.BulletGraph} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjBulletGraph = WjBulletGraphBehavior.register();
function registerV3WjBulletGraph(app) {
    app.component(WjBulletGraphBehavior.tag, WjBulletGraph);
}
var WjRadialGaugeBehavior = /** @class */ (function (_super) {
    __extends(WjRadialGaugeBehavior, _super);
    function WjRadialGaugeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjRadialGaugeBehavior.tag = 'wj-radial-gauge';
    WjRadialGaugeBehavior.props = [
        'isDisabled',
        'tabOrder',
        'value',
        'min',
        'max',
        'origin',
        'isReadOnly',
        'handleWheel',
        'step',
        'format',
        'thickness',
        'hasShadow',
        'isAnimated',
        'showText',
        'showTicks',
        'showTickText',
        'showRanges',
        'stackRanges',
        'thumbSize',
        'tickSpacing',
        'getText',
        'autoScale',
        'startAngle',
        'sweepAngle',
        'needleShape',
        'needleLength',
        'needleElement'
    ];
    WjRadialGaugeBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjRadialGaugeBehavior.changeEvents = {
        'valueChanged': ['value'],
    };
    WjRadialGaugeBehavior.classCtor = function () { return wjcGauge.RadialGauge; };
    WjRadialGaugeBehavior.modelProp = 'value';
    return WjRadialGaugeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.gauge.RadialGauge} control.
 *
 * The <b>wj-radial-gauge</b> component may contain
 * a {@link wijmo.vue2.gauge.WjRange} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.gauge.RadialGauge} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 *
 * The example below shows how to instantiate and initialize a
 * {@link wijmo.gauge.RadialGauge} control using Vue markup:
 *
 * <pre>&lt;wj-radial-gauge
 *     :min="0" :max="1000" :step="50" :is-read-only="false"
 *     format="c0" :thumb-size="12" :show-text="Value"
 *     :show-ranges="false"
 *     :value="sales"
 *     :value-changed="salesChanged"&gt;
 *     &lt;wj-range wj-property="face" :thickness="0.5"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range wj-property="pointer" :thickness="0.5"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="0" :max="333" color="red"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="333" :max="666" color="gold"&gt;
 *     &lt;/wj-range&gt;
 *     &lt;wj-range :min="666" :max="1000" color="green"&gt;
 *     &lt;/wj-range&gt;
 * &lt;/wj-radial-gauge&gt;</pre>
 *
 * The code <b>min</b>, <b>max</b>, <b>step</b>, and <b>isReadOnly</b> properties
 * to define the range of the gauge and to allow users to edit its value.
 * Next, it binds the gauge's <b>value</b> property to a <b>sales</b> variable
 * in the controller.
 *
 * Then it sets the <b>format</b>, <b>thumbSize</b>, and <b>showRanges</b>
 * properties to define the appearance of the gauge. Finally, the markup sets
 * the thickness of the <b>face</b> and <b>pointer</b> ranges, and extra ranges
 * that will control the color of the <b>value</b> range depending on the gauge's
 * current value.
 */
export var WjRadialGauge = WjRadialGaugeBehavior.register();
function registerV3WjRadialGauge(app) {
    app.component(WjRadialGaugeBehavior.tag, WjRadialGauge);
}
var WjRangeBehavior = /** @class */ (function (_super) {
    __extends(WjRangeBehavior, _super);
    function WjRangeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjRangeBehavior.tag = 'wj-range';
    WjRangeBehavior.parentProp = 'ranges';
    WjRangeBehavior.props = [
        'wjProperty',
        'color',
        'min',
        'max',
        'name',
        'thickness'
    ];
    WjRangeBehavior.events = [
        'initialized'
    ];
    WjRangeBehavior.classCtor = function () { return wjcGauge.Range; };
    return WjRangeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.gauge.Range} class.
 *
 * The <b>wj-range</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.gauge.WjLinearGauge}
 * , {@link wijmo.vue2.gauge.WjBulletGraph}
 *  or {@link wijmo.vue2.gauge.WjRadialGauge}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.gauge.Range} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjRange = WjRangeBehavior.register();
function registerV3WjRange(app) {
    app.component(WjRangeBehavior.tag, WjRange);
}
export function registerGauge(app) {
    if (VueApi.isV3Plus) {
        registerV3WjLinearGauge(app);
        registerV3WjBulletGraph(app);
        registerV3WjRadialGauge(app);
        registerV3WjRange(app);
    }
}
